/**
 * Removing contacts — the shared half, used by BOTH places the office can do
 * it (#1303, #1307).
 *
 * The first cut of this shipped only on the **Contacts & Routing** tab, which
 * is not the screen the office actually uses to look at contacts. The plain
 * **Contacts** tab (`?tab=contacts`) is where 1,749 imported Cintas rows are
 * read, and every action there was `disabled={!r.legacyId}` — imported
 * contacts have no legacy row, so verify, edit and delete were all dead
 * exactly where the junk lives. Putting the batching, the wording, and the
 * result-reporting here means the two tabs cannot drift into saying different
 * things about the same delete.
 */
import { api } from "@/lib/api";

/** One person's outcome from POST /contacts/bulk-delete. */
export interface ContactRemovalResult {
    contactId: string;
    status: "deleted" | "blocked" | "not_found";
    name?: string;
    reason?: string;
    suppressed?: number;
}

export interface BulkRemovalResponse {
    deleted: number;
    blocked: number;
    notFound: number;
    suppressed: number;
    results: ContactRemovalResult[];
}

/**
 * The server caps one request at 200 ids, deliberately — an unbounded
 * transaction is not something a malformed client should be able to ask for.
 * Clearing a bad import is exactly the case that exceeds it (Cintas carries
 * 1,749 contacts), so the client chunks rather than making the office press
 * Remove nine times.
 *
 * The client chunk is 50, well under the server's 200. Each contact costs its
 * own transaction and half a dozen queries, so a full-200 request against a
 * big account is a request that runs for a long time with nothing to show for
 * it — long enough to risk a gateway timeout, and if it times out the office
 * cannot tell what landed. Fifty keeps every request short and makes progress
 * visible while a 1,700-row clear-out runs.
 */
export const BULK_REMOVE_CHUNK = 50;

export function chunkIds(ids: string[], size = BULK_REMOVE_CHUNK): string[][] {
    const out: string[][] = [];
    for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
    return out;
}

/**
 * Remove any number of contacts, one capped request at a time, folded back
 * into a single result. Runs the batches in sequence on purpose: each one is
 * a write against the same customer, and firing nine at once buys nothing but
 * lock contention on a table the office is watching.
 */
export async function removeContactsInBatches(
    customerId: string,
    contactIds: string[],
    opts: { onProgress?: (done: number, total: number) => void; post?: typeof api.post } = {},
): Promise<BulkRemovalResponse> {
    const post = opts.post ?? api.post;
    const total: BulkRemovalResponse = { deleted: 0, blocked: 0, notFound: 0, suppressed: 0, results: [] };
    let done = 0;
    for (const batch of chunkIds(contactIds)) {
        const res = await post<BulkRemovalResponse>(`/api/customers/${customerId}/contacts/bulk-delete`, {
            contactIds: batch,
        });
        total.deleted += res.deleted;
        total.blocked += res.blocked;
        total.notFound += res.notFound;
        total.suppressed += res.suppressed;
        total.results.push(...res.results);
        done += batch.length;
        opts.onProgress?.(done, contactIds.length);
    }
    return total;
}

/**
 * What the office is asked before a removal. Says out loud that imported
 * people stay gone, because that is the surprising part: everything else
 * about job-system data comes back on the next sync, and the office has been
 * told so repeatedly.
 */
export function buildRemovalMessage(args: { count: number; importedCount: number; singleName?: string }): string {
    const { count, importedCount, singleName } = args;
    const head =
        count === 1 && singleName
            ? `Remove ${singleName} from this account?`
            : `Remove these ${count} people from this account?`;
    const lines = [head, "Any addresses they have stop receiving documents right away."];
    if (importedCount > 0) {
        lines.push(
            importedCount === count
                ? "They came from the job system, and they will not come back on the next sync. You can restore them from this tab."
                : `${importedCount} of them came from the job system, and will not come back on the next sync. You can restore them from this tab.`,
        );
    }
    return lines.join(" ");
}

/**
 * What the office is told afterwards. A partial result is the interesting
 * case and the one a bare "Removed!" would hide — every blocked person is
 * named with the reason the server gave, never rolled into a count.
 *
 * Blocked people are named up to a limit: after a 1,700-row clear-out, a
 * toast listing four hundred names is the same as no message at all.
 */
export function summarizeRemoval(res: BulkRemovalResponse): { tone: "success" | "warning" | "error"; text: string } {
    const blocked = res.results.filter((r) => r.status === "blocked");
    const parts: string[] = [];
    if (res.deleted > 0) parts.push(`Removed ${res.deleted} ${res.deleted === 1 ? "person" : "people"}`);
    if (res.notFound > 0) parts.push(`${res.notFound} ${res.notFound === 1 ? "was" : "were"} already gone`);
    if (blocked.length > 0) {
        const shown = blocked.slice(0, 3).map((b) => b.reason ?? `${b.name ?? "One contact"} is still in use.`);
        const rest = blocked.length - shown.length;
        parts.push(shown.join(" ") + (rest > 0 ? ` …and ${rest} more still in use.` : ""));
    }
    const text = parts.join(" · ") || "Nothing to remove";
    if (res.deleted === 0 && blocked.length > 0) return { tone: "error", text };
    if (blocked.length > 0) return { tone: "warning", text };
    return { tone: "success", text };
}

/**
 * Which per-row actions are available on the Contacts tab.
 *
 * This is the seam the first cut of #1303 got wrong. Every action on that tab
 * was written as `disabled={!r.legacyId}` — one condition covering three
 * different questions — so when the v2 delete arrived, the delete button
 * stayed dead on imported rows. It is a predicate now so the distinction is
 * stated once, in a place a test can hold it:
 *
 *   · edit and verify still write through the legacy `customer_contacts`
 *     CRUD, so they genuinely need the linked legacy row;
 *   · remove goes through the v2 bulk endpoint, which is keyed on
 *     `contacts.id` and tombstones the ServiceTitan record — so it works on
 *     every row, and imported rows are the whole point of it.
 */
export function contactActionAvailability(row: { legacyId: string | null }): {
    canEdit: boolean;
    canVerify: boolean;
    canRemove: boolean;
} {
    const hasLegacyRow = !!row.legacyId;
    return { canEdit: hasLegacyRow, canVerify: hasLegacyRow, canRemove: true };
}
